// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useCallback } from 'react';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const getStoredToken = () => {
    return localStorage.getItem('token') || sessionStorage.getItem('token') || null;
  };

  const [token, setToken] = useState(getStoredToken);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [expiryTimeoutId, setExpiryTimeoutId] = useState(null);

  // Keep storage in sync (only set when token exists)
  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      sessionStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
    }
  }, [token]);

  // decode and schedule expiration
  useEffect(() => {
    if (!token) {
      setIsTokenExpired(false);
      if (expiryTimeoutId) {
        clearTimeout(expiryTimeoutId);
        setExpiryTimeoutId(null);
      }
      return;
    }

    let decoded;
    try {
      const base64 = token.split('.')[1];
      decoded = JSON.parse(atob(base64));
    } catch (err) {
      // invalid token => treat as expired
      console.error('Error decoding token', err);
      setIsTokenExpired(true);
      setToken(null);
      return;
    }

    if (!decoded?.exp) {
      // no exp => don't schedule but treat as valid
      return;
    }

    const expiryMs = decoded.exp * 1000;
    if (expiryMs <= Date.now()) {
      // already expired
      setIsTokenExpired(true);
      setToken(null);
      return;
    }

    const timeToExpiry = expiryMs - Date.now();

    // schedule a timeout to mark expired
    const id = setTimeout(() => {
      setIsTokenExpired(true);
      setToken(null);
    }, timeToExpiry);

    setExpiryTimeoutId(id);

    return () => {
      clearTimeout(id);
      setExpiryTimeoutId(null);
    };
  }, [token]);

  const login = useCallback((newToken) => {
    setToken(newToken);
    setIsTokenExpired(false);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setIsTokenExpired(false);
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    // force navigation to login
    window.location.replace('/');
  }, []);

  const value = {
    token,
    login,
    logout,
    isTokenExpired,
    setToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
